const ProductDescriptionToggle = (() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OVERFLOW_THRESHOLD = 8;

  const getCollapsedHeight = (root) => {
    const raw = getComputedStyle(root).getPropertyValue('--product-description-collapsed-height').trim();
    if (!raw) return 120;

    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 120;

    if (raw.endsWith('rem')) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return value * rootFontSize;
    }

    if (raw.endsWith('em')) {
      const fontSize = parseFloat(getComputedStyle(root).fontSize) || 16;
      return value * fontSize;
    }

    return value;
  };

  const setLabel = (toggle, expanded) => {
    const label = toggle.querySelector('[data-product-description-label]');
    if (!label) return;
    label.textContent = expanded
      ? (theme.strings.seeLess || 'Show less')
      : (theme.strings.seeMore || 'Show more');
    toggle.setAttribute('aria-expanded', String(expanded));
  };

  const finishOpen = (root, clip) => {
    clip.style.transition = 'none';
    clip.style.height = 'auto';
    clip.offsetHeight;
    root.classList.add('is-expanded');
    root.classList.remove('is-opening');
    requestAnimationFrame(() => {
      clip.style.transition = '';
    });
  };

  const finishClose = (root, clip, collapsedHeight) => {
    clip.style.transition = 'none';
    clip.style.height = `${collapsedHeight}px`;
    clip.offsetHeight;
    root.classList.remove('is-closing');
    requestAnimationFrame(() => {
      clip.style.transition = '';
    });
  };

  const open = (root, clip, inner, toggle) => {
    const collapsedHeight = getCollapsedHeight(root);
    const endHeight = inner.scrollHeight;

    root.classList.add('is-opening');
    clip.style.height = `${collapsedHeight}px`;
    clip.style.overflow = 'hidden';
    setLabel(toggle, true);

    if (prefersReducedMotion) {
      finishOpen(root, clip);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clip.style.height = `${endHeight}px`;
      });
    });

    const onTransitionEnd = (event) => {
      if (event.target !== clip || event.propertyName !== 'height') return;
      clip.removeEventListener('transitionend', onTransitionEnd);
      finishOpen(root, clip);
    };

    clip.addEventListener('transitionend', onTransitionEnd);
  };

  const close = (root, clip, toggle) => {
    const collapsedHeight = getCollapsedHeight(root);
    const startHeight = clip.scrollHeight;

    root.classList.add('is-closing');
    root.classList.remove('is-expanded');
    clip.style.height = `${startHeight}px`;
    clip.style.overflow = 'hidden';
    setLabel(toggle, false);

    if (prefersReducedMotion) {
      finishClose(root, clip, collapsedHeight);
      return;
    }

    requestAnimationFrame(() => {
      clip.style.height = `${collapsedHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.target !== clip || event.propertyName !== 'height') return;
      clip.removeEventListener('transitionend', onTransitionEnd);
      finishClose(root, clip, collapsedHeight);
    };

    clip.addEventListener('transitionend', onTransitionEnd);
  };

  const setup = (root) => {
    if (root.getAttribute('data-product-description-init') === 'true') return;

    const clip = root.querySelector('[data-product-description-clip]');
    const inner = root.querySelector('[data-product-description-inner]');
    const toggle = root.querySelector('[data-product-description-toggle]');

    if (!clip || !inner || !toggle) return;

    root.setAttribute('data-product-description-init', 'true');

    const collapsedHeight = getCollapsedHeight(root);
    const needsToggle = inner.scrollHeight > collapsedHeight + OVERFLOW_THRESHOLD;

    if (!needsToggle) {
      toggle.hidden = true;
      return;
    }

    root.classList.add('is-collapsible');
    clip.style.height = `${collapsedHeight}px`;
    toggle.hidden = false;
    setLabel(toggle, false);

    toggle.addEventListener('click', () => {
      if (root.classList.contains('is-opening') || root.classList.contains('is-closing')) {
        return;
      }

      if (root.classList.contains('is-expanded')) {
        close(root, clip, toggle);
      } else {
        open(root, clip, inner, toggle);
      }
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-description]').forEach(setup);
  };

  return { init };
})();

const updatePickerLabelOnHover = (labelSelector, variantSelector) => {
  const currentLabel = document.querySelector(labelSelector);
  const variants = document.querySelectorAll(variantSelector);

  if (!currentLabel || !variants.length) return;

  const defaultLabel = currentLabel.textContent;

  variants.forEach(variant => {
    const label = variant.getAttribute("data-label");

    variant.addEventListener("mouseenter", () => {
      if (label) currentLabel.textContent = label;
    });

    variant.addEventListener("mouseleave", () => {
      currentLabel.textContent = defaultLabel;
    });
  });
};

const updateColorLabelOnHover = () => {
  updatePickerLabelOnHover("[data-current-label]", ".product-variant:not(.product-variant--size)");
};

const updateSizeLabelOnHover = () => {
  updatePickerLabelOnHover("[data-current-size-label]", ".product-variant--size");
};

const updateBundleTitleOnHover = () => {
  const currentTitle = document.querySelector("[data-current-item-title]");
  const bundleItems = document.querySelectorAll(".bundles-container__img");

  if (!currentTitle || !bundleItems.length) return;

  const defaultTitle = currentTitle.textContent;

  bundleItems.forEach(item => {
    const title = item.getAttribute("data-product-title");

    item.addEventListener("mouseenter", () => {
      if (title) currentTitle.textContent = title;
    });

    item.addEventListener("mouseleave", () => {
      currentTitle.textContent = defaultTitle;
    });
  });
};

const HighlightsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.highlights-grid');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.highlights-grid__item');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;
    const shouldInitOnDesktop = itemCount >= 5;

    // ✅ Initialize conditions
    if ((isMobile || shouldInitOnDesktop) && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: true,
        contain: true,
      });
    } 
    // ✅ Destroy when not mobile and less than 5 items
    else if (!isMobile && itemCount < 5 && slider) {
      slider.destroy();
      slider = null;
    }
  };

  // Optional: debounce resize for performance
  const resizeHandler = () => {
    clearTimeout(window._highlightsSliderTimeout);
    window._highlightsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();

const InstructionsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.instructions-grid');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.instructions-grid__item');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;
    const shouldInitOnDesktop = itemCount > 4;

    if ((isMobile || shouldInitOnDesktop) && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: true,
        contain: true,
      });
    } else if (!isMobile && itemCount <= 4 && slider) {
      slider.destroy();
      slider = null;
    }
  };

  const resizeHandler = () => {
    clearTimeout(window._instructionsSliderTimeout);
    window._instructionsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();

const WarrantyHighlightsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.warranty-section__highlights');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.warranty-section__highlight');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;

    if (isMobile && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: false,
        contain: true,
      });
    } else if (!isMobile && slider) {
      slider.destroy();
      slider = null;
    }
  };

  const resizeHandler = () => {
    clearTimeout(window._warrantyHighlightsSliderTimeout);
    window._warrantyHighlightsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();


const ProductFaqAccordion = (() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const finishOpen = (details, content) => {
    content.style.transition = 'none';
    content.style.height = `${content.scrollHeight}px`;
    content.offsetHeight;
    content.style.removeProperty('height');
    content.style.overflow = '';
    details.classList.remove('is-opening');
    requestAnimationFrame(() => {
      content.style.transition = '';
    });
  };

  const finishClose = (details, content) => {
    content.style.transition = 'none';
    details.removeAttribute('open');
    content.style.removeProperty('height');
    content.style.overflow = '';
    details.classList.remove('is-closing');
    requestAnimationFrame(() => {
      content.style.transition = '';
    });
  };

  const openItem = (details, content) => {
    details.setAttribute('open', '');
    details.classList.add('is-opening');
    content.style.height = '0px';
    content.style.overflow = 'hidden';

    if (prefersReducedMotion) {
      finishOpen(details, content);
      return;
    }

    requestAnimationFrame(() => {
      const endHeight = content.scrollHeight;

      requestAnimationFrame(() => {
        content.style.height = `${endHeight}px`;
      });
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onTransitionEnd);
      finishOpen(details, content);
    };

    content.addEventListener('transitionend', onTransitionEnd);
  };

  const closeItem = (details, content) => {
    if (prefersReducedMotion) {
      finishClose(details, content);
      return;
    }

    details.classList.add('is-closing');
    const startHeight = content.scrollHeight;
    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      content.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onTransitionEnd);
      finishClose(details, content);
    };

    content.addEventListener('transitionend', onTransitionEnd);
  };

  const init = () => {
    document.querySelectorAll('.product-faq__item:not([data-faq-init])').forEach((details) => {
      const summary = details.querySelector('.product-faq__trigger');
      const content = details.querySelector('.product-faq__content');

      if (!summary || !content) return;

      details.setAttribute('data-faq-init', 'true');

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (details.classList.contains('is-opening') || details.classList.contains('is-closing')) {
          return;
        }

        if (details.hasAttribute('open')) {
          closeItem(details, content);
        } else {
          openItem(details, content);
        }
      });
    });
  };

  return { init };
})();

const UgcVideos = (() => {
  const SWIPE_THRESHOLD = 60;
  const DESKTOP_MIN = 769;
  const MODAL_ANIMATION_MS = 350;

  class UgcVideosInstance {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('.ugc-videos__modal');
      this.sliderEl = root.querySelector('.ugc-videos__slider');
      this.cards = [...root.querySelectorAll('[data-ugc-card]')];
      this.slides = this.modal ? [...this.modal.querySelectorAll('[data-ugc-modal-slide]')] : [];
      this.productLists = this.modal ? [...this.modal.querySelectorAll('[data-ugc-products-list]')] : [];
      this.videoCount = parseInt(root.dataset.videoCount, 10) || this.slides.length;
      this.activeIndex = 0;
      this.isModalOpen = false;
      this.isModalClosing = false;
      this.isMuted = true;
      this.slider = null;
      this.productsSliders = new Map();
      this.lastFocus = null;
      this.touchStartY = 0;
      this.touchStartX = 0;
      this.chatElements = null;

      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.bindEvents();
      this.initSlider();
      this.initAjaxForms();
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize);
    }

    bindEvents() {
      this.cards.forEach((card) => {
        const video = card.querySelector('.ugc-videos__video');
        const playBtn = card.querySelector('.ugc-videos__play-btn');
        const muteBtn = card.querySelector('.ugc-videos__mute-btn');

        card.addEventListener('click', (event) => {
          if (event.target.closest('.ugc-videos__mute-btn')) return;
          const index = parseInt(card.dataset.index, 10);
          this.openModal(index);
        });

        card.addEventListener('mouseenter', () => {
          if (!this.isDesktop() || !video) return;
          card.classList.add('is-playing');
          if (muteBtn) muteBtn.hidden = false;
          video.muted = true;
          video.play().catch(() => {});
        });

        card.addEventListener('mouseleave', () => {
          if (!video) return;
          card.classList.remove('is-playing');
          video.pause();
          video.currentTime = 0;
          video.muted = true;
          if (muteBtn) {
            muteBtn.hidden = true;
            this.updateMuteButton(muteBtn, true);
          }
        });

        if (muteBtn && video) {
          muteBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            video.muted = !video.muted;
            this.updateMuteButton(muteBtn, video.muted);
          });
        }
      });

      this.modal.querySelectorAll('[data-ugc-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      const modalInner = this.modal.querySelector('.ugc-videos__modal-inner');
      if (modalInner) {
        modalInner.addEventListener('click', (event) => {
          if (!this.isDesktop() || !this.isModalOpen || this.isModalClosing) return;
          if (event.target.closest('.ugc-videos__modal-stage')) return;
          this.closeModal();
        });
      }

      const prevBtn = this.modal.querySelector('[data-ugc-prev]');
      const nextBtn = this.modal.querySelector('[data-ugc-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      const modalMuteBtn = this.modal.querySelector('[data-ugc-modal-mute]');
      if (modalMuteBtn) {
        modalMuteBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.isMuted = !this.isMuted;
          this.applyModalMute();
          this.updateMuteButton(modalMuteBtn, this.isMuted);
        });
      }

      const swipeArea = this.modal.querySelector('[data-ugc-swipe-area]');
      if (swipeArea) {
        swipeArea.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        swipeArea.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        swipeArea.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      this.modal.querySelectorAll('.ugc-videos__product-form').forEach((form) => {
        form.addEventListener('click', (event) => event.stopPropagation());
        form.addEventListener('submit', (event) => event.stopPropagation());
      });

      document.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    onKeyDown(event) {
      if (!this.isModalOpen) return;
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
      if (event.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
    }

    onTouchStart(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.touches[0];
      this.touchStartY = touch.clientY;
      this.touchStartX = touch.clientX;
    }

    onTouchMove(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.touches[0];
      const deltaY = Math.abs(touch.clientY - this.touchStartY);
      const deltaX = Math.abs(touch.clientX - this.touchStartX);
      if (deltaY > deltaX && deltaY > 10) {
        event.preventDefault();
      }
    }

    onTouchEnd(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.changedTouches[0];
      const deltaY = touch.clientY - this.touchStartY;
      const deltaX = touch.clientX - this.touchStartX;
      if (Math.abs(deltaY) < SWIPE_THRESHOLD || Math.abs(deltaY) < Math.abs(deltaX)) return;
      if (deltaY > 0) this.goTo(this.activeIndex + 1);
      else this.goTo(this.activeIndex - 1);
    }

    isDesktop() {
      return window.innerWidth >= DESKTOP_MIN;
    }

    initSlider() {
      if (!this.sliderEl || this.videoCount <= 1) {
        this.destroySlider();
        return;
      }

      if (this.isDesktop()) {
        this.destroySlider();
        return;
      }

      if (this.slider) return;

      this.slider = new Flickity(this.sliderEl, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        contain: true,
      });
    }

    destroySlider() {
      if (!this.slider) return;
      this.slider.destroy();
      this.slider = null;
    }

    onResize() {
      this.initSlider();
    }

    updateMuteButton(button, isMuted) {
      button.setAttribute('aria-pressed', String(isMuted));
      button.setAttribute(
        'aria-label',
        isMuted ? 'Unmute video' : 'Mute video'
      );
      const mutedIcon = button.querySelector('.ugc-videos__mute-icon--muted');
      const unmutedIcon = button.querySelector('.ugc-videos__mute-icon--unmuted');
      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
    }

    suppressShopifyChat() {
      const elements = [
        document.getElementById('ShopifyChat'),
        ...document.querySelectorAll('inbox-online-store-chat'),
      ].filter(Boolean);

      this.chatElements = elements;
      elements.forEach((el) => {
        if (el.dataset.ugcChatZIndex === undefined) {
          el.dataset.ugcChatZIndex = el.style.zIndex || '';
        }
        el.style.setProperty('z-index', '2147483646', 'important');
      });
    }

    restoreShopifyChat() {
      (this.chatElements || []).forEach((el) => {
        if (el.dataset.ugcChatZIndex !== undefined) {
          if (el.dataset.ugcChatZIndex) {
            el.style.zIndex = el.dataset.ugcChatZIndex;
          } else {
            el.style.removeProperty('z-index');
          }
          delete el.dataset.ugcChatZIndex;
        }
      });
      this.chatElements = null;
    }

    openModal(index) {
      if (!this.modal || this.isModalOpen || this.isModalClosing) return;
      this.lastFocus = document.activeElement;
      this.isModalOpen = true;
      this.activeIndex = index;
      this.modal.hidden = false;
      this.modal.classList.remove('is-open');
      document.body.classList.add('ugc-videos-modal-open');
      this.suppressShopifyChat();
      this.showSlide(index);
      this.initAjaxForms();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
      });

      const closeBtn = this.modal.querySelector(
        this.isDesktop() ? '.ugc-videos__modal-close--desktop' : '.ugc-videos__modal-close--mobile'
      );
      if (closeBtn) closeBtn.focus();
    }

    finishCloseModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.classList.remove('is-open');
      this.isModalClosing = false;
      document.body.classList.remove('ugc-videos-modal-open');
      this.restoreShopifyChat();
      this.pauseAllModalVideos();
      this.destroyMobileProductsSliders();
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    closeModal() {
      if (!this.modal || !this.isModalOpen || this.isModalClosing) return;
      this.isModalOpen = false;
      this.isModalClosing = true;
      this.modal.classList.remove('is-open');

      const inner = this.modal.querySelector('.ugc-videos__modal-inner');
      let didFinish = false;
      const finish = () => {
        if (didFinish) return;
        didFinish = true;
        if (inner) inner.removeEventListener('transitionend', onTransitionEnd);
        this.finishCloseModal();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== inner || event.propertyName !== 'transform') return;
        finish();
      };

      if (inner) {
        inner.addEventListener('transitionend', onTransitionEnd);
      }

      window.setTimeout(finish, MODAL_ANIMATION_MS + 50);
    }

    initAjaxForms() {
      if (typeof theme === 'undefined' || !theme.AjaxProduct) return;

      this.modal.querySelectorAll('.ugc-videos__product-form:not([data-ajax-init])').forEach((form) => {
        new theme.AjaxProduct(form, '[data-add-to-cart]');
        form.setAttribute('data-ajax-init', 'true');
      });
    }

    pauseAllModalVideos() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('.ugc-videos__video--modal');
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        video.onended = null;
      });
    }

    getWrappedIndex(index) {
      if (this.videoCount <= 0) return 0;
      return (index + this.videoCount) % this.videoCount;
    }

    goTo(index) {
      const nextIndex = this.getWrappedIndex(index);
      if (nextIndex === this.activeIndex) {
        if (this.videoCount === 1) {
          this.replayActiveVideo();
        }
        return;
      }
      this.activeIndex = nextIndex;
      this.showSlide(nextIndex);
    }

    replayActiveVideo() {
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;
      const video = activeSlide.querySelector('.ugc-videos__video--modal');
      if (!video) return;
      video.currentTime = 0;
      video.muted = this.isMuted;
      video.play().catch(() => {});
    }

    showSlide(index) {
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
        const video = slide.querySelector('.ugc-videos__video--modal');
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.onended = null;
          if (!isActive) return;
          video.muted = this.isMuted;
          video.onended = () => this.goTo(index + 1);
          video.play().catch(() => {});
        }
      });

      this.productLists.forEach((list, i) => {
        const isActive = i === index;
        list.classList.toggle('is-active', isActive);
        list.hidden = !isActive;
      });

      this.initMobileProductsSlider(index);
      this.applyModalMute();
    }

    applyModalMute() {
      const modalMuteBtn = this.modal.querySelector('[data-ugc-modal-mute]');
      if (modalMuteBtn) this.updateMuteButton(modalMuteBtn, this.isMuted);

      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;
      const video = activeSlide.querySelector('.ugc-videos__video--modal');
      if (video) video.muted = this.isMuted;
    }

    destroyMobileProductsSliders() {
      this.productsSliders.forEach((slider) => {
        slider.element.querySelectorAll('.ugc-videos__modal-products-cell:not(.ugc-videos__modal-products-cell--gutter)').forEach((cell) => {
          cell.style.width = '';
          cell.style.marginLeft = '';
          cell.style.marginRight = '';
        });
        slider.element.querySelectorAll('.ugc-videos__modal-products-cell--gutter').forEach((gutter) => gutter.remove());
        slider.destroy();
      });
      this.productsSliders.clear();
    }

    initMobileProductsSlider(index) {
      if (this.isDesktop()) return;

      this.destroyMobileProductsSliders();

      const slide = this.slides[index];
      if (!slide) return;
      const sliderEl = slide.querySelector('[data-ugc-products-slider]');
      if (!sliderEl) return;

      const productCells = [...sliderEl.querySelectorAll(':scope > .ugc-videos__modal-products-cell:not(.ugc-videos__modal-products-cell--gutter)')];
      if (productCells.length <= 1) return;

      const gap = 12;
      const leadingGutter = document.createElement('div');
      leadingGutter.className = 'ugc-videos__modal-products-cell ugc-videos__modal-products-cell--gutter';
      leadingGutter.setAttribute('aria-hidden', 'true');

      const trailingGutter = document.createElement('div');
      trailingGutter.className = 'ugc-videos__modal-products-cell ugc-videos__modal-products-cell--gutter';
      trailingGutter.setAttribute('aria-hidden', 'true');

      sliderEl.insertBefore(leadingGutter, sliderEl.firstChild);
      sliderEl.appendChild(trailingGutter);

      productCells.forEach((cell) => {
        cell.style.width = '85%';
        cell.style.marginRight = `${gap}px`;
        cell.style.marginLeft = '';
      });

      const slider = new Flickity(sliderEl, {
        adaptiveHeight: false,
        pageDots: false,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        contain: true,
        initialIndex: 0,
      });

      this.productsSliders.set(index, slider);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('ugc-videos:not([data-ugc-init])').forEach((root) => {
      root.setAttribute('data-ugc-init', 'true');
      new UgcVideosInstance(root);
    });
  };

  return { init };
})();


const PropelFormI18n = (() => {
  const configNode = document.getElementById('mw-warranty-translations');
  if (!/\/a\/pr\//.test(location.pathname) || !configNode) return { setup() {} };
  let config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (error) {
    return { setup() {} };
  }
  const locale = (document.documentElement.lang || config.locale || 'en').toLowerCase().split('-')[0];
  const decoder = document.createElement('textarea');
  const strings = Object.fromEntries(Object.entries(config.strings).map(([key, value]) => {
    decoder.innerHTML = value;
    return [key, decoder.value];
  }));
  const normalize = (value) => String(value || '').replace(/\u00a0/g, ' ').replace(/[\u2018\u2019\u02bc]/g, "'").replace(/[\u201c\u201d]/g, '"').replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();
  const source = {
  "registration_title": "Product Registration – 10-Year Warranty",
  "claim_title": "Warranty Support",
  "choose_product": "Choose a product",
  "search_product": "Search for your product here",
  "serial_number": "Serial number",
  "serial_summary": "Serial:",
  "seller_name": "Store/Seller name",
  "document_number": "Document number",
  "purchase_date": "Purchase date",
  "first_name": "First name",
  "last_name": "Last name",
  "country": "Country",
  "email": "Email",
  "next": "Next",
  "back": "Back",
  "submit": "Submit",
  "register": "Register",
  "submit_claim": "Submit claim",
  "required": "Required",
  "incorrect_number": "Incorrect number",
  "invalid_email": "Please enter a valid email address",
  "error_heading": "Before you can register, the errors below must be addressed:",
  "registration_success": "Registration successful",
  "registration_success_message": "Your product has been registered successfully.",
  "claim_success": "Claim submitted",
  "claim_success_message": "Your claim has been submitted.",
  "registration_intro": "Register your Masuria Wild product and enjoy a 10-year warranty!",
  "registration_instruction": "Fill out the form below to activate your warranty and receive exclusive offers for Masuria Wild customers.",
  "serial_help": "You'll find the serial number on the inner wall of your fire pit (see illustration below).",
  "required_instruction": "All required fields must be completed.",
  "confirmation_instruction": "You will receive a confirmation email within 2–3 business days.",
  "contact_instruction": "If you need help in another language or have any questions, just write to us via the chat in the bottom right corner of the screen.",
  "claim_intro": "We're here to help!",
  "claim_instruction": "If you have a problem with your Masuria Wild product or need warranty support, please fill out the form below. Our team will review your request and get back to you as soon as possible.",
  "no_results": "No results found",
  "loading": "Loading...",
  "search": "Search",
  "cancel": "Cancel",
  "close": "Close",
  "select": "Select",
  "date_picker": "Show date picker"
};
  const aliases = {
    'Invalid number': 'incorrect_number',
    'Invalid email': 'invalid_email',
    'Invalid email address': 'invalid_email',
    'Email is invalid': 'invalid_email',
    'Email must be a valid email': 'invalid_email',
    'Please enter a valid email address.': 'invalid_email',
    'Claim successful': 'claim_success',
    'Your claim has been submitted successfully.': 'claim_success_message',
    'No results': 'no_results',
    'No options': 'no_results'
  };
  const lookup = new Map();
  Object.entries(source).forEach(([key, value]) => lookup.set(normalize(value), strings[key]));
  Object.entries(aliases).forEach(([value, key]) => lookup.set(normalize(value), strings[key]));
  const translateValue = (value) => {
    if (typeof value !== 'string' || !value) return value;
    const key = normalize(value);
    const hit = lookup.get(key);
    if (hit) return hit;
    const required = key.match(/^(.+?)\s+is required\.?$/i);
    if (required) {
      const field = lookup.get(normalize(required[1])) || required[1];
      return strings.required_field.replace('[field]', field);
    }
    const emailError = key.match(/^.+?\s+(?:must be a valid email|is not a valid email(?: address)?)\.?$/i);
    if (emailError) return strings.invalid_email;
    const marked = key.match(/^(.+?)\s*\*$/);
    if (marked && lookup.has(normalize(marked[1]))) return `${lookup.get(normalize(marked[1]))} *`;
    if (value.includes('\n')) return value.split('\n').map(translateValue).join('\n');
    return value;
  };
  const translateDeep = (value) => {
    if (typeof value === 'string') return translateValue(value);
    if (Array.isArray(value)) return value.map(translateDeep);
    if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, translateDeep(item)]));
    return value;
  };
  const isFormApiUrl = (url) => {
    try {
      const parsed = new URL(url, location.href);
      return parsed.origin === location.origin && /\/a\/pr\/api\/storefront\/forms\/(1578|1580)\/?$/.test(parsed.pathname);
    } catch (error) {
      return false;
    }
  };
  if (locale !== 'en') {
    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        const url = typeof args[0] === 'string' || args[0] instanceof URL ? String(args[0]) : args[0]?.url;
        const method = args[1]?.method || args[0]?.method || 'GET';
        if (String(method).toUpperCase() !== 'GET' || !isFormApiUrl(url) || !response.ok) return response;
        try {
          const body = translateDeep(await response.clone().json());
          const headers = new Headers(response.headers);
          headers.delete('content-length');
          headers.delete('content-encoding');
          return new Response(JSON.stringify(body), { status: response.status, statusText: response.statusText, headers });
        } catch (error) {
          return response;
        }
      };
    }
    const xhr = window.XMLHttpRequest?.prototype;
    if (xhr) {
      const originalOpen = xhr.open;
      const textDescriptor = Object.getOwnPropertyDescriptor(xhr, 'responseText');
      const responseDescriptor = Object.getOwnPropertyDescriptor(xhr, 'response');
      const requests = new WeakMap();
      xhr.open = function (method, url, ...rest) {
        requests.set(this, { matches: String(method).toUpperCase() === 'GET' && isFormApiUrl(url) });
        return originalOpen.call(this, method, url, ...rest);
      };
      const translatedText = (request, original) => {
        const state = requests.get(request);
        if (!state?.matches || request.readyState !== 4 || request.status < 200 || request.status >= 300) return original;
        if (state.original === original) return state.translated;
        try {
          state.translated = JSON.stringify(translateDeep(JSON.parse(original)));
          state.original = original;
          return state.translated;
        } catch (error) {
          return original;
        }
      };
      if (textDescriptor?.get && textDescriptor.configurable) Object.defineProperty(xhr, 'responseText', {
        ...textDescriptor,
        get() { return translatedText(this, textDescriptor.get.call(this)); }
      });
      if (responseDescriptor?.get && responseDescriptor.configurable) Object.defineProperty(xhr, 'response', {
        ...responseDescriptor,
        get() {
          const original = responseDescriptor.get.call(this);
          const state = requests.get(this);
          if (!state?.matches || this.readyState !== 4 || this.status < 200 || this.status >= 300) return original;
          if (this.responseType === '' || this.responseType === 'text') return translatedText(this, original);
          if (this.responseType === 'json') return translateDeep(original);
          return original;
        }
      });
    }
  }
  const ignored = (element) => !element || element.closest('script, style, noscript, textarea, input, [contenteditable="true"], [role="option"]');
  const translateRoot = (root) => {
    const owner = root.ownerDocument;
    const walker = owner.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (ignored(node.parentElement)) continue;
      const original = node.nodeValue;
      const translated = translateValue(original);
      if (translated !== original && normalize(translated) !== normalize(original)) node.nodeValue = `${original.match(/^\s*/)[0]}${translated}${original.match(/\s*$/)[0]}`;
    }
    root.querySelectorAll('[placeholder], [aria-label], [title], input[type="submit"], input[type="button"]').forEach((element) => {
      for (const attribute of ['placeholder', 'aria-label', 'title']) {
        const original = element.getAttribute(attribute);
        if (!original) continue;
        const translated = translateValue(original);
        if (translated !== original) element.setAttribute(attribute, translated);
      }
      if (element.matches('input[type="submit"], input[type="button"]')) {
        const translated = translateValue(element.value);
        if (translated !== element.value) element.value = translated;
      }
    });
  };
  const observedDocuments = new WeakSet();
  const observedFrames = new WeakSet();
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; apply(); });
  };
  const observe = (doc) => {
    if (!doc?.documentElement || observedDocuments.has(doc)) return;
    observedDocuments.add(doc);
    new MutationObserver(schedule).observe(doc, { childList: true, subtree: true, characterData: true });
  };
  const apply = () => {
    const heading = document.getElementById('propel-registration-heading');
    if (heading) translateRoot(heading);
    document.querySelectorAll('#MainContent iframe[title="Iframe Wrapper"]').forEach((frame) => {
      if (!observedFrames.has(frame)) {
        observedFrames.add(frame);
        frame.addEventListener('load', schedule);
      }
      try {
        const doc = frame.contentDocument;
        if (!doc?.documentElement) return;
        if (doc.documentElement.lang !== locale) doc.documentElement.lang = locale;
        observe(doc);
        if (doc.body) translateRoot(doc.body);
      } catch (error) {
        return;
      }
    });
  };
  const setup = () => { observe(document); apply(); };
  setup();
  document.addEventListener('DOMContentLoaded', setup, { once: true });
  return { setup };
})();

const CartUpsell = (() => {
  let adding = false;

  const syncFromMarkup = (text) => {
    const html = document.createElement('div');
    html.innerHTML = text;
    const source = html.querySelector('[data-cart-upsell]');
    const targets = document.querySelectorAll('[data-cart-upsell]');
    if (!targets.length) return;

    const markup = source ? source.innerHTML : '';
    targets.forEach((target) => {
      target.innerHTML = markup;
    });
  };

  const patchCartMarkup = () => {
    if (typeof theme === 'undefined' || !theme.CartForm || !theme.CartForm.prototype) return;
    if (theme.CartForm.prototype.__cartUpsellPatched) return;

    const originalCartMarkup = theme.CartForm.prototype.cartMarkup;
    theme.CartForm.prototype.cartMarkup = function (text) {
      originalCartMarkup.call(this, text);
      syncFromMarkup(text);
    };
    theme.CartForm.prototype.__cartUpsellPatched = true;
  };

  const addToCart = (button) => {
    if (adding || !button || !theme.routes || !theme.routes.cartAdd) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    adding = true;
    button.classList.add('btn--loading');
    button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 422) {
          if (data.description || data.message) {
            alert(data.description || data.message);
          }
          return;
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: data,
            addToCartBtn: button,
          },
        }));

        if (document.body.classList.contains('template-cart')) {
          window.scrollTo(0, 0);
          window.location.reload();
          return;
        }

        if (theme.settings && theme.settings.cartType === 'page') {
          window.location = theme.routes.cartPage;
          return;
        }

        // Drawer cart rebuilds via the ajaxProduct:added listener on CartDrawer.
        // Also rebuild any page cart forms if present.
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(() => {})
      .finally(() => {
        adding = false;
        button.classList.remove('btn--loading');
        button.disabled = false;
      });
  };

  const onClick = (event) => {
    const button = event.target.closest('[data-cart-upsell-add]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    addToCart(button);
  };

  const init = () => {
    patchCartMarkup();
    document.addEventListener('click', onClick);
  };

  return { init };
})();

document.addEventListener("DOMContentLoaded", () => {
  ProductDescriptionToggle.init();
  updateColorLabelOnHover();
  updateSizeLabelOnHover();
  updateBundleTitleOnHover();
  HighlightsSlider.setup();
  InstructionsSlider.setup();
  WarrantyHighlightsSlider.setup();
  ProductFaqAccordion.init();
  UgcVideos.init();
  PropelFormI18n.setup();
  CartUpsell.init();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-product-description]')) {
    ProductDescriptionToggle.init(event.target);
  }
  if (event.target.querySelector('.product-faq__list')) {
    ProductFaqAccordion.init();
  }
  if (event.target.querySelector('ugc-videos')) {
    UgcVideos.init(event.target);
  }
  if (event.target.querySelector('.warranty-section__highlights')) {
    WarrantyHighlightsSlider.setup();
  }
});

const ReviewAccessibility = (() => {
  const selector = '.jdgm-sort-dropdown-wrapper, .jdgm-revs-tab-btn, .jdgm-medal';
  const apply = () => {
    const strings = window.theme && window.theme.strings;
    if (!strings || !strings.reviewSort) return;
    document.querySelectorAll('.jdgm-sort-dropdown-wrapper').forEach(label => {
      const caption = label.querySelector('.jdgm-sort-dropdown-label');
      if (caption && caption.textContent !== strings.reviewSort) caption.textContent = strings.reviewSort;
      label.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === 'Sort by') {
          node.textContent = strings.reviewSort + ' ';
        }
      });
      const select = label.querySelector('select');
      if (select) select.setAttribute('aria-label', strings.reviewSort);
    });
    document.querySelectorAll('.jdgm-revs-tab-btn').forEach(button => {
      button.setAttribute('aria-label', strings.reviewOpen);
    });
    document.querySelectorAll('.jdgm-medal').forEach(medal => {
      const label = medal.getAttribute('aria-label') || '';
      const key = label.includes('Transparent Shop medal') ? 'reviewTransparent'
        : label.includes('Verified Reviews Shop medal') ? 'reviewVerified'
        : label.includes('Monthly Record Shop medal') ? 'reviewMonthly' : null;
      if (key) medal.setAttribute('aria-label', strings[key]);
    });
  };
  const init = () => {
    apply();
    let queued = false;
    new MutationObserver(records => {
      const relevant = records.some(record => [...record.addedNodes].some(node =>
        node.nodeType === Node.ELEMENT_NODE && (node.matches(selector) || node.querySelector(selector))
      ));
      if (!relevant || queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; apply(); });
    }).observe(document.body, { childList: true, subtree: true });
  };
  return { init };
})();
document.addEventListener('DOMContentLoaded', ReviewAccessibility.init);
